import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { FaChevronDown, FaPlus, FaSearch } from 'react-icons/fa';
import GlobalSearchApi from '../../api/GlobalSearchApi';
import EditLayoutModal from '../layout/EditLayoutModal';
import ResizableTable from '../common/ResizableTable';
import { getListViewStyleRowActions } from '../../utils/listViewRowActions';
import { getSearchFieldDisplayText } from './globalSearchUi';
import './GlobalSearch.css';

/** Build ResizableTable row shape from global-search record (same as fetchRecords / ListView). */
function toTableData(group) {
  const cols = group.columns || [];
  return (group.records || []).map((rec) => {
    const row = {
      id: {
        value: rec.id,
        label: 'ID',
        type: 'uuid',
      },
    };
    cols.forEach((col) => {
      const f = rec.values?.[col.fieldName];
      if (f && typeof f === 'object' && !Array.isArray(f)) {
        row[col.fieldName] = {
          value: f.value ?? null,
          displayValue: f.displayValue ?? null,
          type: f.type || col.dataType || null,
          label: f.label || col.fieldLabel || col.fieldName,
          isReference: f.isReference === true,
          referenceObject: f.referenceObject || null,
        };
      } else {
        row[col.fieldName] = {
          value: f ?? null,
          displayValue: null,
          type: col.dataType || null,
          label: col.fieldLabel || col.fieldName,
          isReference: false,
          referenceObject: null,
        };
      }
    });
    return row;
  });
}

function toTableColumns(group) {
  return (group.columns || []).map((col) => {
    const dataType = String(col.dataType || '').toLowerCase();
    const isNumeric = [
      'currency',
      'number',
      'decimal',
      'integer',
      'rollupsummary',
      'double',
      'percent',
    ].includes(dataType);
    return {
      field: col.fieldName,
      label: col.fieldLabel || col.fieldName,
      sortable: true,
      dataType: col.dataType || '',
      align: isNumeric ? 'right' : 'left',
    };
  });
}

function sortGroupRecords(group, sort) {
  const list = [...(group.records || [])];
  if (sort !== 'az') return list;
  const titleField = group.columns?.[0]?.fieldName;
  return list.sort((a, b) => {
    const an =
      getSearchFieldDisplayText(
        a.values?.[titleField],
        group.columns?.[0]?.dataType,
        titleField
      ) || String(a.id || '');
    const bn =
      getSearchFieldDisplayText(
        b.values?.[titleField],
        group.columns?.[0]?.dataType,
        titleField
      ) || String(b.id || '');
    return an.localeCompare(bn);
  });
}

export default function GlobalSearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = (searchParams.get('q') || '').trim();
  const objectParam = (searchParams.get('object') || '').trim();
  const [draftQ, setDraftQ] = useState(q);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [groups, setGroups] = useState([]);
  const [allowedObjects, setAllowedObjects] = useState([]);
  const [filter, setFilter] = useState(objectParam || 'all');
  const [sort, setSort] = useState('rel');
  const [objectMenuOpen, setObjectMenuOpen] = useState(false);
  const [objectFilterText, setObjectFilterText] = useState('');
  const [createObjectName, setCreateObjectName] = useState(null);
  const objectInputRef = useRef(null);

  useEffect(() => {
    setDraftQ(q);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const api = new GlobalSearchApi();
      const objects =
        objectParam && objectParam !== 'all' ? objectParam : undefined;
      const res = await api.search({
        q: q.length >= 2 ? q : '',
        limit: 50,
        objects,
      });
      if (res?.data?.success) {
        const data = res.data.data || {};
        setGroups(data.groups || []);
        setAllowedObjects(data.allowedObjects || []);
      } else {
        setError(res?.data?.message || 'Search failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [q, objectParam]);

  useEffect(() => {
    setFilter(objectParam || 'all');
    load();
  }, [load, objectParam]);

  useEffect(() => {
    if (!objectMenuOpen) return undefined;
    const onDoc = (e) => {
      if (!e.target.closest?.('.gs-results-object-combo')) {
        setObjectMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [objectMenuOpen]);

  const scopeOptions = useMemo(
    () => [
      { key: 'all', label: 'All objects' },
      ...allowedObjects.map((o) => ({
        key: o.objectName,
        label: o.pluralLabel || o.objectLabel || o.objectName,
      })),
    ],
    [allowedObjects]
  );

  const filteredObjectOptions = useMemo(() => {
    const text = objectFilterText.trim().toLowerCase();
    if (!text) return scopeOptions;
    return scopeOptions.filter((o) => {
      const label = String(o.label || '').toLowerCase();
      const key = String(o.key || '').toLowerCase();
      return label.includes(text) || key.includes(text);
    });
  }, [scopeOptions, objectFilterText]);

  const selectedObjectMeta = useMemo(() => {
    if (!filter || filter === 'all') return null;
    return (
      allowedObjects.find(
        (o) =>
          String(o.objectName).toLowerCase() === String(filter).toLowerCase()
      ) || null
    );
  }, [allowedObjects, filter]);

  const objectDisplayLabel = selectedObjectMeta
    ? selectedObjectMeta.pluralLabel || selectedObjectMeta.objectLabel
    : 'All objects';

  const creatableObjects = useMemo(() => {
    let list = (allowedObjects || []).filter((o) => o.canCreate);
    if (filter && filter !== 'all') {
      list = list.filter(
        (o) =>
          String(o.objectName).toLowerCase() === String(filter).toLowerCase()
      );
    }
    return list;
  }, [allowedObjects, filter]);

  const setObjectFilter = (next) => {
    const value = !next || next === 'all' ? 'all' : next;
    setFilter(value);
    setObjectMenuOpen(false);
    setObjectFilterText('');
    const params = new URLSearchParams(searchParams);
    if (value === 'all') {
      params.delete('object');
    } else {
      params.set('object', value);
    }
    if (q) params.set('q', q);
    setSearchParams(params, { replace: true });
  };

  const applyQuery = (nextQ) => {
    const trimmed = String(nextQ || '').trim();
    const params = new URLSearchParams();
    if (trimmed) params.set('q', trimmed);
    if (filter && filter !== 'all') params.set('object', filter);
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  const openCreateModal = (objectName) => {
    if (!objectName) return;
    setCreateObjectName(objectName);
  };

  const closeCreateModal = () => {
    setCreateObjectName(null);
  };

  const handleCreateSaved = () => {
    closeCreateModal();
    load();
  };

  const visibleGroups = useMemo(() => {
    let list = groups;
    if (filter !== 'all') {
      list = groups.filter(
        (g) => String(g.objectName).toLowerCase() === filter.toLowerCase()
      );
      if (
        list.length === 0 &&
        selectedObjectMeta &&
        q.length >= 2
      ) {
        list = [
          {
            objectName: selectedObjectMeta.objectName,
            objectLabel: selectedObjectMeta.objectLabel,
            pluralLabel: selectedObjectMeta.pluralLabel,
            canCreate: selectedObjectMeta.canCreate,
            columns: [],
            records: [],
          },
        ];
      }
    }
    if (sort === 'type') {
      list = [...list].sort((a, b) =>
        String(a.pluralLabel || a.objectLabel || a.objectName).localeCompare(
          String(b.pluralLabel || b.objectLabel || b.objectName)
        )
      );
    }
    return list.map((g) => ({
      ...g,
      records: sortGroupRecords(g, sort),
    }));
  }, [groups, filter, sort, selectedObjectMeta, q]);

  const total = groups.reduce((n, g) => n + (g.records?.length || 0), 0);

  const placeholder = selectedObjectMeta
    ? `Search ${selectedObjectMeta.pluralLabel || selectedObjectMeta.objectLabel}…`
    : 'Search all records';

  return (
    <div className="gs-results-page">
      <div className="gs-results">
        <div className="gs-results-toolbar">
          <h1 className="gs-results-heading">Search results</h1>
          <p className="gs-results-sub">
            {q.length < 2
              ? 'Type at least 2 characters to search every object allowed for global search'
              : `${total} records across ${groups.length} objects for “${q}”`}
          </p>

          <div className="gs-results-bar">
            <div
              className={`gs-results-object-combo${
                objectMenuOpen ? ' is-open' : ''
              }`}
            >
              <button
                type="button"
                className="gs-results-object-combo-toggle"
                aria-expanded={objectMenuOpen}
                aria-haspopup="listbox"
                aria-label="Search in object"
                onClick={() => {
                  setObjectMenuOpen((v) => !v);
                  setTimeout(() => objectInputRef.current?.focus(), 0);
                }}
              >
                <span className="gs-results-object-combo-label text-truncate">
                  {objectDisplayLabel}
                </span>
                <FaChevronDown size={10} aria-hidden="true" />
              </button>
              {objectMenuOpen ? (
                <div className="gs-results-object-combo-menu" role="listbox">
                  <input
                    ref={objectInputRef}
                    type="search"
                    className="gs-results-object-combo-filter"
                    value={objectFilterText}
                    onChange={(e) => setObjectFilterText(e.target.value)}
                    placeholder="Type object name…"
                    aria-label="Filter objects"
                    autoComplete="off"
                  />
                  {filteredObjectOptions.length === 0 ? (
                    <div className="gs-results-object-combo-empty">
                      No matching objects
                    </div>
                  ) : (
                    filteredObjectOptions.map((o) => (
                      <button
                        key={o.key || 'all'}
                        type="button"
                        role="option"
                        className={`gs-results-object-combo-option${
                          filter === o.key ||
                          (filter === 'all' && o.key === 'all')
                            ? ' is-selected'
                            : ''
                        }`}
                        onClick={() => setObjectFilter(o.key)}
                      >
                        {o.label}
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>

            <label className="gs-results-field">
              <FaSearch className="gs-results-field-icon" aria-hidden="true" />
              <input
                type="search"
                value={draftQ}
                placeholder={placeholder}
                autoComplete="off"
                aria-label="Search query"
                onChange={(e) => setDraftQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyQuery(draftQ);
                  }
                }}
              />
            </label>
            <select
              className="gs-results-sort"
              value={sort}
              aria-label="Sort results"
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="rel">Best Match</option>
              <option value="az">Name A–Z</option>
              <option value="type">Object Type</option>
            </select>
          </div>

          {creatableObjects.length > 0 ? (
            <div className="gs-results-new-actions" aria-label="Create records">
              {creatableObjects.slice(0, 8).map((o) => (
                <button
                  key={o.objectName}
                  type="button"
                  className="gs-results-new-btn"
                  onClick={() => openCreateModal(o.objectName)}
                >
                  <FaPlus size={11} aria-hidden="true" />
                  New {o.objectLabel || o.objectName}
                </button>
              ))}
            </div>
          ) : null}

          {error ? <Alert variant="danger" className="mb-0 mt-3">{error}</Alert> : null}

          {q.length >= 2 ? (
            <div className="gs-results-tabs" role="tablist" aria-label="Filter by object">
              <button
                type="button"
                className={`gs-chip${filter === 'all' ? ' is-on' : ''}`}
                onClick={() => setObjectFilter('all')}
              >
                All
                <span className="gs-chip-count">{total}</span>
              </button>
              {groups.map((g) => (
                <button
                  key={g.objectName}
                  type="button"
                  className={`gs-chip${filter === g.objectName ? ' is-on' : ''}`}
                  onClick={() => setObjectFilter(g.objectName)}
                >
                  {g.pluralLabel || g.objectLabel}
                  <span className="gs-chip-count">{g.records?.length || 0}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : null}

        {!loading && q.length >= 2 && visibleGroups.length === 0 ? (
          <div className="gs-results-section">
            <div className="gs-results-empty">
              <strong>No records match “{q}”</strong>
              Try a name, email, phone number, or company.
              {creatableObjects.length > 0 ? (
                <div className="gs-results-empty-actions">
                  {creatableObjects.slice(0, 4).map((o) => (
                    <button
                      key={o.objectName}
                      type="button"
                      className="gs-results-new-btn is-primary"
                      onClick={() => openCreateModal(o.objectName)}
                    >
                      <FaPlus size={11} aria-hidden="true" />
                      New {o.objectLabel || o.objectName}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {!loading &&
          visibleGroups.map((g) => {
            const tableData = toTableData(g);
            const tableColumns = toTableColumns(g);
            const recordLinkField =
              tableColumns.find((c) => c.field === 'name')?.field ||
              tableColumns[0]?.field ||
              'name';
            const canCreate =
              g.canCreate === true ||
              allowedObjects.some(
                (o) =>
                  String(o.objectName).toLowerCase() ===
                    String(g.objectName).toLowerCase() && o.canCreate
              );
            return (
              <section key={g.objectName} className="gs-results-section">
                <header className="gs-results-section-head">
                  <strong>{g.pluralLabel || g.objectLabel}</strong>
                  <div className="gs-results-section-head-right">
                    <span className="gs-results-section-count">
                      {tableData.length} result
                      {tableData.length === 1 ? '' : 's'}
                    </span>
                    {canCreate ? (
                      <button
                        type="button"
                        className="gs-results-section-new"
                        onClick={() => openCreateModal(g.objectName)}
                      >
                        <FaPlus size={11} aria-hidden="true" />
                        New {g.objectLabel || g.objectName}
                      </button>
                    ) : null}
                  </div>
                </header>
                {tableData.length === 0 ? (
                  <div className="gs-results-empty">
                    No matching records
                    {canCreate ? (
                      <div className="gs-results-empty-actions">
                        <button
                          type="button"
                          className="gs-results-new-btn is-primary"
                          onClick={() => openCreateModal(g.objectName)}
                        >
                          <FaPlus size={11} aria-hidden="true" />
                          New {g.objectLabel || g.objectName}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="gs-results-table-wrap">
                    <ResizableTable
                      data={tableData}
                      columns={tableColumns}
                      loading={false}
                      error={null}
                      showSerialNumber
                      showActions
                      actions={getListViewStyleRowActions(g.objectName)}
                      headerActions={[]}
                      showSearch={false}
                      showPagination={false}
                      showNewButton={false}
                      selectedRows={[]}
                      onRowSelectionChange={() => {}}
                      totalRecords={tableData.length}
                      recordsPerPage={tableData.length}
                      currentPage={1}
                      onPageChange={() => {}}
                      sortBy=""
                      sortOrder="asc"
                      onSort={() => {}}
                      emptyMessage="No matching records"
                      objectName={g.objectName}
                      recordLinkField={recordLinkField}
                      sourcePage="listview"
                      className="gs-results-resizable-table"
                      tableClassName="table-theme"
                      exportFilename={`${g.objectName}_search`}
                      exportSheetName={g.objectName}
                      onRefresh={load}
                    />
                  </div>
                )}
              </section>
            );
          })}
      </div>

      {createObjectName ? (
        <EditLayoutModal
          show
          onHide={closeCreateModal}
          objectName={createObjectName}
          sourcePage="listview"
          onSave={handleCreateSaved}
          onCancel={closeCreateModal}
          onRefresh={load}
        />
      ) : null}
    </div>
  );
}
